// hooks/useSubtitleManager.js
import { useRef, useCallback } from "react";
import srtParser2 from "srt-parser-2";
import { useVideo } from "../context/VideoContext";
import { useSelectedSubtitles } from "../context/SelectedSubtitlesContext";
// 🎯 ایمپورت توابع جدید
import {
  readFileWithProperEncoding,
  srtToWebVTT,
  parseSRTFileWithEncoding,
} from "../utils/subtitleEncoding";

const useSubtitleManager = (videoRef) => {
  const subtitle1JsonRef = useRef([]);
  const subtitle2JsonRef = useRef([]);
  const cueListenersRef = useRef({});
  const { setSubtitle1Json, setSubtitle2Json } = useVideo();
  const { setActiveSubtitle } = useSelectedSubtitles();

  // پاک کردن همه trackها
  const clearAllTracks = useCallback(() => {
    if (!videoRef.current) return;

    const track1 = document.getElementById("subtitle-track-1");
    if (track1 && track1.parentNode === videoRef.current) {
      videoRef.current.removeChild(track1);
    }

    const track2 = document.getElementById("subtitle-track-2");
    if (track2 && track2.parentNode === videoRef.current) {
      videoRef.current.removeChild(track2);
    }
  }, [videoRef]);

  const setupSubtitle = useCallback(
    async (file, lang, subNumber, onCueChange, metadata = null) => {
      if (!videoRef.current) return false;

      try {
        const trackId = `subtitle-track-${subNumber}`;
        const video = videoRef.current;

        // حذف track قبلی
        const oldTrack = document.getElementById(trackId);
        if (oldTrack && oldTrack.parentNode === video) {
          video.removeChild(oldTrack);
        }

        // حذف listener قبلی
        if (cueListenersRef.current[subNumber]) {
          const textTracks = video.textTracks;
          for (let i = 0; i < textTracks.length; i++) {
            if (textTracks[i].id === trackId) {
              textTracks[i].removeEventListener(
                "cuechange",
                cueListenersRef.current[subNumber],
              );
            }
          }
          cueListenersRef.current[subNumber] = null;
        }

        // 🎯 تبدیل فایل با کدگذاری درست
        const srtText = await readFileWithProperEncoding(file);
        const vttText = srtToWebVTT(srtText);
        const blob = new Blob([vttText], { type: "text/vtt" });
        const vttUrl = URL.createObjectURL(blob);

        const track = document.createElement("track");
        track.id = trackId;
        track.kind = "subtitles";
        track.src = vttUrl;
        track.srclang = lang;
        track.default = false;

        video.appendChild(track);

        // 🎯 پارس JSON با تابع جدید
        const jsonResult = await parseSRTFileWithEncoding(file);
        if (subNumber === 1) {
          subtitle1JsonRef.current = jsonResult;
          setSubtitle1Json(jsonResult);
        } else {
          subtitle2JsonRef.current = jsonResult;
          setSubtitle2Json(jsonResult);
        }

        // ذخیره در context
        if (metadata) {
          const subtitleData = {
            ...metadata,
            language: lang,
            fileName: file.name,
            cues: jsonResult,
            rawContent: srtText,
            source:
              metadata.source ||
              metadata.sourceType ||
              (metadata.onlineId ? "online" : "local_file"),
            sourceType:
              metadata.sourceType ||
              metadata.source ||
              (metadata.onlineId ? "online" : "local_file"),
            onlineId: metadata.onlineId || metadata.subtitlesUploadId || null,
            subtitlesUploadId: metadata.subtitlesUploadId || null,
            subOneID: metadata.subOneID || null,
            subTwoID: metadata.subTwoID || null,
          };
          await setActiveSubtitle(subtitleData, subNumber, file);
        }

        const setupTrackListener = () => {
          const textTracks = video.textTracks;
          if (!textTracks) return false;

          for (let i = 0; i < textTracks.length; i++) {
            const t = textTracks[i];
            if (t.id === trackId) {
              t.mode = "hidden";

              // 🔥 اصلاح شده: متن رو مستقیم از TextTrack می‌گیریم
              const handler = () => {
                const activeCues = t.activeCues;
                if (activeCues && activeCues.length > 0) {
                  // مستقیم متن رو از خود activeCue بگیر، نه از JSON
                  if (onCueChange) {
                    onCueChange(activeCues[0].text);
                  }
                } else if (onCueChange) {
                  onCueChange("");
                }
              };

              t.addEventListener("cuechange", handler);
              cueListenersRef.current[subNumber] = handler;
              return true;
            }
          }
          return false;
        };

        let attempts = 0;
        const interval = setInterval(() => {
          if (setupTrackListener() || attempts > 10) {
            clearInterval(interval);
          }
          attempts++;
        }, 100);

        return true;
      } catch (error) {
        console.error(`Error in setupSubtitle ${subNumber}:`, error);
        return false;
      }
    },
    [videoRef, setSubtitle1Json, setSubtitle2Json, setActiveSubtitle],
  );

  return {
    setupSubtitle,
    clearAllTracks,
    subtitle1JsonRef,
    subtitle2JsonRef,
  };
};

export default useSubtitleManager;
